import { Controller } from "@hotwired/stimulus";

// Bridges the draggable venue marker on the admin
// location preview map to the venue update form.
//
// On every drag-end, the location_map controller
// emits `location-map:originDragged` with
// `{ latitude, longitude }` in the event detail.
// This controller writes those coordinates to the
// hidden lat/lng inputs nested under
// `venue[address_attributes]` so that saving the
// slideout persists the dragged coordinates. It
// also flips the `skip_geocode` hidden input so
// the Address `after_validation :geocode` callback
// does NOT overwrite the admin's tweak.
//
// "Pin adjusted / Reset to address" badge appears
// after a drag; the reset link clears the override
// and snaps the marker back to the original
// (geocoded) coordinates the form was loaded with.
export default class extends Controller {
  static targets = [
    "latitude",
    "longitude",
    "skipGeocode",
    "badge",
  ];
  static values = {
    mapId: { type: String, default: "" },
    originalLatitude: { type: String, default: "" },
    originalLongitude: { type: String, default: "" },
  };

  pinDragged(event) {
    const { latitude, longitude } = event.detail;
    if (latitude == null || longitude == null) return;

    this.writeOverride(latitude, longitude);
    this.showBadge();
  }

  // The "Reset to address" link in the badge.
  // Clears the override fields and snaps the map
  // marker back to the originally geocoded position
  // by writing the original lat/lng onto the map's
  // data attributes (the location-map controller's
  // value-changed callbacks pick them up and re-
  // render the marker in place).
  reset(event) {
    event?.preventDefault();
    this.writeOverride("", "");
    this.hideBadge();
    this.snapMarkerBackToOriginal();
  }

  writeOverride(latitude, longitude) {
    // Enable / disable in lockstep with value
    // presence. Disabled inputs are NOT submitted
    // by the browser, which is what keeps a
    // normal "Save location" with no drag from
    // mass-assigning empty `latitude`/`longitude`
    // and clearing the persisted coords.
    const active = Boolean(latitude && longitude);

    if (this.hasLatitudeTarget) {
      this.latitudeTarget.value =
        active ? latitude : "";
      this.latitudeTarget.disabled = !active;
    }
    if (this.hasLongitudeTarget) {
      this.longitudeTarget.value =
        active ? longitude : "";
      this.longitudeTarget.disabled = !active;
    }
    if (this.hasSkipGeocodeTarget) {
      this.skipGeocodeTarget.value =
        active ? "1" : "";
      this.skipGeocodeTarget.disabled = !active;
    }
  }

  snapMarkerBackToOriginal() {
    if (!this.mapIdValue) return;
    if (!this.originalLatitudeValue) return;
    if (!this.originalLongitudeValue) return;

    const map = document.getElementById(
      this.mapIdValue
    );
    if (!map) return;

    map.dataset.locationMapLatitudeValue =
      this.originalLatitudeValue;
    map.dataset.locationMapLongitudeValue =
      this.originalLongitudeValue;
  }

  // We toggle classList instead of the `hidden`
  // attribute because the badge uses `flex` for
  // layout, and Tailwind utility `display: flex`
  // beats UA `[hidden] { display: none }` by
  // specificity, leaving the badge visible.
  showBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove("hidden");
      this.badgeTarget.classList.add("flex");
    }
  }

  hideBadge() {
    if (this.hasBadgeTarget) {
      this.badgeTarget.classList.remove("flex");
      this.badgeTarget.classList.add("hidden");
    }
  }
}
